import { createInstance } from 'i18next';
import { initReactI18next } from 'react-i18next';

const i18n = createInstance();

export type AppLanguage = 'en' | 'es';

const defaultLanguage: AppLanguage = 'en';
const loadedLanguages = new Set<AppLanguage>();

const translationLoaders: Record<AppLanguage, () => Promise<Record<string, unknown>>> = {
  en: () => import('./locales/en').then((module) => module.default),
  es: () => import('./locales/es').then((module) => module.default),
};

let initPromise: Promise<typeof i18n> | null = null;

function isSupportedLanguage(value: string | null): value is AppLanguage {
  return value === 'en' || value === 'es';
}

function resolveLanguage(value: string | null | undefined): AppLanguage {
  const normalizedValue = value ?? null;
  return isSupportedLanguage(normalizedValue) ? normalizedValue : defaultLanguage;
}

function resolveBrowserLanguage(): AppLanguage {
  if (typeof navigator === 'undefined') {
    return defaultLanguage;
  }

  const candidates = [...(navigator.languages || []), navigator.language].filter(Boolean);
  return candidates.some((value) => value.toLowerCase().startsWith('es')) ? 'es' : defaultLanguage;
}

async function loadLanguageResources(language: AppLanguage): Promise<void> {
  if (loadedLanguages.has(language)) {
    return;
  }

  const translations = await translationLoaders[language]();
  i18n.addResourceBundle(language, 'translation', translations, true, true);
  loadedLanguages.add(language);
}

export async function initializeI18n(): Promise<typeof i18n> {
  if (initPromise) {
    return initPromise;
  }

  initPromise = (async () => {
    const storedLanguage = typeof localStorage === 'undefined' ? null : localStorage.getItem('language');
    const initialLanguage = storedLanguage ? resolveLanguage(storedLanguage) : resolveBrowserLanguage();
    const initialTranslations = await translationLoaders[initialLanguage]();

    await i18n.use(initReactI18next).init({
      resources: {
        [initialLanguage]: {
          translation: initialTranslations,
        },
      },
      lng: initialLanguage,
      fallbackLng: defaultLanguage,
      interpolation: {
        escapeValue: false,
      },
      react: {
        useSuspense: false,
      },
    });

    loadedLanguages.add(initialLanguage);

    return i18n;
  })();

  return initPromise;
}

export async function changeAppLanguage(language: AppLanguage): Promise<void> {
  await initializeI18n();
  await loadLanguageResources(language);
  await i18n.changeLanguage(language);
  localStorage.setItem('language', language);
}

export default i18n;
